import type { FeedbackType } from "@asurion-core/expert-workspace-feedback";
import type { AgentActivity, GaiaPayloadConfigs, Partner } from "@expert/shared-types";
import type { GuideAppConfig } from "@expert/solve-tools";

export interface DataCenterMapping {
    /** Nashville Data Center */
    NDC: string;

    /** Nashville Gulch Data Center */
    NGD: string;

    /** Sterling Data Center */
    SDC: string;
}

export interface TwilioAppConfig {
    // Services IDs
    conversationServiceSid: string;
    syncServiceSid: string;
    taskRouterWorkspaceSid: string;

    holdMusicUrl: string;

    // Outbound call configuration
    taskRouterWorkflowSid: string;
    taskRouterDefaultTaskQueueSid: Record<Partner, string>;
    taskRouterWorkerActivityTechnicalIssues: string;

    sip: {
        Ips: {
            Transfer: DataCenterMapping;
            Outbound: DataCenterMapping;
        };
        Interconnect: {
            Ashburn: DataCenterMapping;
            SanJose: DataCenterMapping;
        };
    };

    // Outbound call FROM original value is used from Orbit API
    // Fallback #1 for Verizon - used with SIP
    outboundCallFromSip: Record<Partner, string>;
    // Fallback #2 for all partners - Twilio registered phone number
    outboundCallFromTwilio: string;
}

export interface AmazonConnectInstanceInfo {
    region: string;
    instanceId: string;
    instanceAlias: string;
    secondaryInstanceAlias?: string;
    identityProvider?: { loginUrl: string; searchParamName: string };
}

export type AmazonConnectInstancesInfo = Record<Partner, AmazonConnectInstanceInfo>;

interface PartnerProgram {
    name: string;
    claimEligible: boolean;
    survey: {
        generalSurveyId: string;
        claimsSurveyId: string;
    };
}

/**
 * Config options for features enabled by an {@link AgentActivity} that signals
 * technical issues.
 */
export interface TechnicalIssuesConfig {
    /** The name of the dedicated {@link AgentActivity} for technical issues. */
    agentActivity: AgentActivity;
    /**
     * Enables automatically setting the agent's activity to the specified
     * {@link TechnicalIssuesConfig.agentActivity | agentActivity} whenever
     * technical issues are encountered (audio disconnect).
     */
    autoSet?: boolean;
    /**
     * Enables outbound calls whenever the agent's activity is set to the
     * specified {@link TechnicalIssuesConfig.agentActivity | agentActivity}
     * even if the partner configuration does not have outbound calls enabled.
     */
    enableOutboundCalls?: boolean;
}

type SessionSummaryStates = "autosend" | "manualCopy" | "maximum";
export type sessionSummaryTimesConfig = Record<SessionSummaryStates, number>;

interface Features {
    callbackLater: boolean;
    callbackEnabled: boolean;
    outboundCall: boolean;
    surveyEnabled: boolean;
    groupingChatQueueEnabled: boolean;
    postCallNotepadRedaction: boolean;
    customerPanelEnabled: boolean;
    isEndCallResolutionMenuEnabled: boolean;
    isExpertPerformanceDashboardEnabled: boolean;
    isRealtimeTranscriptEnabled: boolean;
    logOutOnOfflineAgentActivity: boolean;
    callControlsEnabled: boolean;
    transferListEnabled: boolean;
    medialess?: boolean;
    isUnifiedSalesViewEnabled: boolean;
    csdEnabled: boolean;
    feedback: boolean;
    feedbackType: FeedbackType;
    isEnhancedCustomerSearchEnabled: boolean;
    isDigitalCompetencyInsightEnabled: boolean;
    simplifiedSummary: boolean;
    ozmo: boolean;
    disableAgentStatusChange: boolean;
    verificationType: "phoneNumber" | "billingAccountNumberAndPin";
    isCustomerDetailsV2Enabled?: boolean;
    isCustomerDroppedNotificationEnabled?: boolean;
    programSelectionEnabled?: boolean;
    isResolutionStatusFormEnabled?: boolean;
    isOfflineAgentActivitySelectable?: boolean;
    cleanMissedCalls?: boolean;
    isSalesQuestionnaireEnabled?: boolean;
    isChallengesEnabled: boolean;
    isExpertUniversityEnabled: boolean;
    /** Toggles between react-international-phone and MaskedInput for MdnInput.tsx. */
    isInternationalPhoneEnabled?: boolean;
    /** Toggles between legacy transfer menu and new transfer menu that supports international phone inputs. */
    isSegmentedTransferMenuEnabled?: boolean;
    /** Default country code for phone inputs e.g "US" which notifies UI to use "+1". */
    defaultCountry?: string;
    /** Countries enabled for outbound dialing. */
    clientEnabledCountries?: string[];
    /** Enables legacy VDN input for transfers. */
    isVdnTransferInputEnabled?: boolean;
    /** Allows user to select between multiple countries for phone inputs. */
    countrySelectEnabled?: boolean;
    isRealTimeComplianceCheckEnabled?: boolean;
}

type AdditionalVerificationField = "email" | "orderId";

export const leaderboardMetrics = Object.freeze(["calls", "sales"] as const);
export type LeaderboardMetric = (typeof leaderboardMetrics)[number];
export function isLeaderboardMetric(value: unknown): value is LeaderboardMetric {
    return (leaderboardMetrics as readonly unknown[]).includes(value);
}
export interface LeaderboardConfig {
    controlText: string;
    description: string;
    metric: LeaderboardMetric;
    reminder: string;
    sku?: string;
}

interface PerformanceMetricConfig {
    body: string;
    header: string;
    metric:
        | "totalCalls"
        | "callResolutionTime"
        | "oneCallResolution"
        | "resolutionRate"
        | "transferRate"
        | "perfectSurveys"
        | "fiveStarRating"
        | "entireRepPerformance"
        | "salesPer100"
        | "cancellationRate"
        | "netEnrolls";
}

export type Channeled<T> = T | { chat: T; voice: T };

export interface PartnerConfig {
    features: Features;
    activationSupportNumbers: string[];
    activationSupportQueues?: string[];
    additionalVerificationFields?: AdditionalVerificationField[];
    programs: PartnerProgram[];
    requiresManualCopy: boolean;
    extraSessionSummaryPayloadProps?: Record<string, string>;
    accountNumberLength?: number;
    allowedAccountNumberLengths?: number[];
    partnerTool: string;
    partnerToolUrl?: string;
    performanceMetrics: PerformanceMetricConfig[];
    orderIdMask?: string;
    /** Options enabled by a dedicated {@link AgentActivity} for technical issues. */
    technicalIssues?: TechnicalIssuesConfig;
    /** The {@link AgentActivity} to set while waiting to perform a callback. */
    callbackPendingAgentActivity?: AgentActivity;
    trainingAgentActivity?: AgentActivity;
    wrappingAgentActivity?: AgentActivity;
    defaultAgentActivity?: AgentActivity;
    solveGuidedFlows?: GuideAppConfig;
    contactStateTypesToAutoClear?: string[];
    gaiaSummaryModel: string;
    maxSummaryGenerationTime?: number;
    sessionSummaryTimes?: sessionSummaryTimesConfig;
    blocklistedPrefixes?: string[];
    blocklistedConfirmationCodes?: string[];
    blocklistedNumbers?: string[];
    survey?: SurveyConfig;
    gaiaPayloadProps: GaiaPayloadConfigs;
    sessionSummary?: SessionSummaryConfig;
    salesQuestionnaireProps?: SalesQuestionnaire;
    leaderboards: LeaderboardConfig[];
    pacingTargets?: PacingTargetsConfig;
    partnerEventWhitelist?: string[];
    amazonConnectInstancesInfoConfigKey?: string;
}

export interface PacingTargetsConfig {
    enabled: boolean;
    defaultTargetSeconds: number;
    salesEligibleBufferSeconds: number;
    callTypeTargets: Record<string, number>;
    approachingThreshold: number;
    exceededThreshold: number;
}

export type PartnerAppConfig = Record<Partner, PartnerConfig>;

interface QueryBySearchTypeConfig {
    clientChannelId: string;
    query: string;
}

export interface PartnerSearchConfig {
    queriesBySearchType: { phoneNumber?: QueryBySearchTypeConfig[] };
}

export type PartnerSearchAppConfig = Record<Partner, PartnerSearchConfig>;

export interface AppConfig {
    amazonConnectInstancesInfo?: AmazonConnectInstancesInfo;
    twilioAppConfig?: TwilioAppConfig;
    partnersConfig: PartnerAppConfig;
    partnerSearch?: PartnerSearchAppConfig;
    allowedPartners: string;
}

export interface AppConfigSingleton {
    amazonConnectInstancesInfo?: AmazonConnectInstancesInfo;
    twilioAppConfig?: TwilioAppConfig;
    getPartnersConfig: (partner: Partner) => PartnerConfig;
    getPartnerSearch?: (partner: Partner) => PartnerSearchConfig;
    allowedPartners: string[];
    hasPartnerConfig: (partner: Partner) => boolean;
}

export type CallReason = { reason: string; subReasons?: string[] };
export interface SessionSummaryConfig {
    callReasons?: CallReason[];
    dispositions?: string[];
    // TODO: Add all session summary configs here for better organization
}

export interface SalesQuestionnaire {
    plans?: string[];
    affiliates?: string[];
}

export interface SurveyConfig {
    claimsSurveyId?: string;
    generalSurveyId: string;
    isClaimsEligible: boolean;
    useCustomerAgreementProgram: boolean;
    ignoreProgram?: boolean;
    excludedPrograms?: string[];
    shouldSurveyNotResolved?: boolean;
    unresolvedSurveyId?: string;
}
